import {type ClassValue, clsx} from "clsx";
import {twMerge} from "tailwind-merge";
import axios from "axios";
import {defineStore} from "pinia";
import {Ref, ref} from "vue";
import {getOrderFromID, Groups, reportOverview, TitleList, userList} from "@/types/Order";
import DriveInformationDialog from "@/components/dialogs/DriveInformationDialog.vue";
import {getMotorFromId, global_headers} from "@/types/Drive";
import {DataTableHeader} from "vuetify/lib/components/VDataTable/types";
import FilterForm from "@/components/forms/FilterForm.vue";
import TaskDialog from "@/components/dialogs/TaskDialog.vue";
import OrderDialog from "@/components/dialogs/OrderDialog.vue";
/*import {Variant as textVariant} from "vuetify/lib/composables/variant";
import {Variant as buttonVariant} from "vuetify/lib/components/VField/VField";*/
import {useI18n} from "vue-i18n";
import i18n from "@/i18n";
import FilterHelperDialog from "@/components/dialogs/FilterHelperDialog.vue";
import {supabase} from "@/lib/supabase";
import {useAuth} from "@/lib/useAuth";

export const API_BASE_URL = process.env.VUE_APP_API_URL || "/api";

export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 120000,
});

// Hilfsfunktionen für Case-Konvertierung
export function snakeToCamel(str: string): string {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

export function camelToSnake(str: string): string {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
}

export function convertKeys(obj: any, converter: (key: string) => string): any {
    if (Array.isArray(obj)) {
        return obj.map(item => convertKeys(item, converter))
    }
    if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            acc[converter(key)] = convertKeys(obj[key], converter)
            return acc
        }, {} as any)
    }
    return obj
}

// Response Interceptor: snake_case → camelCase + 401 Handler
apiClient.interceptors.response.use(
    (response) => {
/*        if (response.data) {
            response.data = convertKeys(response.data, snakeToCamel)
        }*/
        return response
    },
    async (error) => {
        if (error.response?.status === 401) {
            console.warn('401 Unauthorized - logging out')
            await supabase.auth.signOut()
            window.location.href = '/login'
        }
        return Promise.reject(error)
    }
)

// Request Interceptor: camelCase → snake_case für POST/PUT/PATCH
apiClient.interceptors.request.use(async (config) => {
    const {data: {session}} = await supabase.auth.getSession()

    if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`
    }

    // Konvertiere Request-Daten von camelCase zu snake_case
/*    if (config.data && typeof config.data === 'object') {
        config.data = convertKeys(config.data, camelToSnake)
    }*/

    return config
})




export const APP_NAME = "SineChart";

/** Gibt den aktuellen Username zurück (aus Profil, Fallback auf user_metadata) */
export function getCurrentUser(): string {
    const auth = useAuth()
    return auth.profile.value?.id
        ?? auth.user.value?.user_metadata?.username
        ?? ""
}

/** @deprecated Nutze getCurrentUser() — dieser Wert ist beim Import evtl. noch leer */
export const currentUser = "";

export const {t} = i18n.global;

type textVariant = 'filled' | 'outlined' | 'plain' | 'underlined' | 'solo' | 'solo-inverted' | 'solo-filled';
type buttonVariant = 'flat' | 'text' | 'elevated' | 'tonal' | 'outlined' | 'plain';

export const useGlobalSetting = defineStore("global", {
    state: () => ({
        currentGroup: Groups.tde,
        //currentUser: userList.beckm,
        globalReadonly: true,
        hasChanged: false,
        isMobile: ref(window.innerWidth <= 900),
        isLoading: true,
        loadingMessage: useI18n().t('loading.application'),
        globalTextVariant: "solo" as textVariant,
        globalButtonVariant: "elevated" as buttonVariant,
    })
});

export interface PopupOptions {
    contentFilled?: boolean
    dialogTitle?: string
    maxWidth?: number
    fullscreen?: boolean
    persistent?: boolean
    model2?: any
}

export const useMainPopup = defineStore('mainPopup', () => {
    const popupRef: Ref<any> = ref(null)

    function setPopupRef(ref: any) {
        popupRef.value = ref
    }

    function open(component: any, props = {}, options: PopupOptions = {}) {
        if (popupRef.value) {
            popupRef.value.open(component, props, options)
        } else {
            console.warn('Popup-Referenz ist nicht gesetzt!')
        }
    }

    function close() {
        if (popupRef.value) {
            popupRef.value.close()
        }
    }

    return {
        popupRef,
        setPopupRef,
        open,
        close
    }
});

export const useSecondPopup = defineStore('secondPopup', () => {
    const popupRef: Ref<any> = ref(null)

    function setPopupRef(ref: any) {
        popupRef.value = ref
    }

    function open(component: any, props = {}, options: PopupOptions = {}) {
        if (popupRef.value) {
            popupRef.value.open(component, props, options)
        } else {
            console.warn('Popup-Referenz ist nicht gesetzt!')
        }
    }

    function close() {
        if (popupRef.value) {
            popupRef.value.close()
        }
    }

    return {
        popupRef,
        setPopupRef,
        open,
        close
    }
});

//utilities

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export const decimalToBinary = (decimal: number): string => {
    return (decimal >>> 0).toString(2);
}

export const decimalToBinaryArray = (decimal: number): Array<Boolean> => {
    const masked = decimal & 0b1111111111;

    const binary = masked.toString(2).padStart(10, "0");

    return Array.from(binary, char => char === "1").reverse();
}

export async function openDriveInformationDialog(id: string, useMain: boolean = true) {
    getMotorFromId(id).then(data => {
        data.position = `${data.location_row}${data.location_col} ${data.location_level}`;
        if (useMain) {
            useMainPopup().open(
                DriveInformationDialog,
                {
                    modelValue: data,
                    headers: global_headers,
                    readonly: true,
                },
                {
                    contentFilled: true,
                    maxWidth: 1200,
                    fullscreen: useGlobalSetting().isMobile,
                    persistent: false,
                }
            );
        } else {
            useSecondPopup().open(
                DriveInformationDialog,
                {
                    modelValue: data,
                    headers: global_headers,
                    readonly: true,
                },
                {
                    contentFilled: true,
                    maxWidth: 1200,
                    fullscreen: false,
                    persistent: false,
                }
            );
        }
    });
}

export async function openTaskInformationDialog(order_id: number, id: number, currentTab?: string) {
    getOrderFromID(order_id).then(data => {
        useMainPopup().open(
            TaskDialog,
            {
                modelValue: data,
                id: id,
                currentTab: currentTab
            },
            {
                contentFilled: true,
                maxWidth: 1200,
                fullscreen: useGlobalSetting().isMobile,
                persistent: true,
            }
        );
    });
}

export async function openOrderInformationDialog(id: number) {
    getOrderFromID(id).then(data => {
        useMainPopup().open(
            OrderDialog,
            {
                modelValue: data,
                id: id
            },
            {
                contentFilled: true,
                maxWidth: 1200,
                fullscreen: useGlobalSetting().isMobile,
                persistent: true,
            }
        );
    });
}

export function openFilterDialog(searchTerms: Ref<any>, sortBy: Ref<any>, fetchData: () => Promise<void>, headers: DataTableHeader[]) {
    useMainPopup().open(
        FilterForm,
        {
            fetch: fetchData,
            headers: headers,
            modelValue: searchTerms,
        },
        {
            contentFilled: false,
            maxWidth: 1200,
            fullscreen: useGlobalSetting().isMobile,
            persistent: false,
            dialogTitle: "Erweiterter Filter",
            model2: sortBy
        }
    );
}

export function openFilterSyntaxDialog() {
    useSecondPopup().open(
        FilterHelperDialog,
        {},
        {
            contentFilled: true,
            maxWidth: 600,
            fullscreen: false,
            persistent: false,
            dialogTitle: "Filtersyntax",
        }
    );
}


//setter


//getter

/*export const getAssetList = async (): Promise<AssetTitleItem[]> => {
    useGlobalSetting().loadingMessage = t("loading.getAssetList")
    try {
        const response = await apiClient.get(`/getAssetList`);
        console.log(response.data);
        return response.data
    } catch (error) {
        throw new Error("Fehler beim Laden der Liste:", {cause: error})
    }
}*/

export const getAuftragsTitleList = async (): Promise<TitleList[]> => {
    useGlobalSetting().loadingMessage = t("loading.auftragsTitleList")
    try {
        const response = await apiClient.get(`/getAuftragsTitleList`);
        return response.data
    } catch (error) {
        throw new Error("Fehler beim Laden der Liste:", {cause: error})
    }
}

export const getAufgabenTitleList = async (): Promise<TitleList[]> => {
    useGlobalSetting().loadingMessage = t("loading.aufgabenTitleList")
    try {
        const response = await apiClient.get(`/getAufgabenTitleList`);
        return response.data
    } catch (error) {
        throw new Error("Fehler beim Laden der Liste:", {cause: error})
    }
}

export const getReportingOverview = async (): Promise<reportOverview> => {
    useGlobalSetting().loadingMessage = t("loading.getReportingOverview")
    useGlobalSetting().isLoading = true;
    try {
        const response = await apiClient.get(`/getReportingOverview`);
        useGlobalSetting().isLoading = false;
        return response.data
    } catch (error) {
        console.error('Fehler beim Laden der Liste:', error)
        useGlobalSetting().isLoading = false;
        return {
            "aufgabenFailed": -1,
            "aufgabenFinished": -1,
            "aufgabenLength": -1,
            "auftragFailed": -1,
            "auftragFinished": -1,
            "auftragsLength": -1,
            "lockoutsFailed": -1,
            "lockoutsFinished": -1,
            "lockoutsLength": -1
        }
    }
}

export function getInitials(name: string): string {
    if (!name?.trim()) return ''
    const parts = name.trim().split(/\s+/)

    if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase()
    }

    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function getColorFromName(name: string): string {
    const colors = [
        '#E53935', // Rot
        '#D81B60', // Pink
        '#8E24AA', // Lila
        '#5E35B1', // Deep Purple
        '#3949AB', // Indigo
        '#1E88E5', // Blau
        '#039BE5', // Light Blue
        '#00ACC1', // Cyan
        '#00897B', // Teal
        '#43A047', // Grün
        '#7CB342', // Light Green
        '#C0CA33', // Lime
        '#FDD835', // Gelb
        '#FFB300', // Amber
        '#FB8C00', // Orange
        '#F4511E', // Deep Orange
    ]

    if (!name?.trim()) return '#9E9E9E'

    let hash = 0
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }

    return colors[Math.abs(hash) % colors.length]
}

